import { Application, DesiredModule, GrantRule, SelfModule } from '../lib/types';
import { PermissionMatrix } from './PermissionMatrix';
import { Checkbox, Empty, Field } from './ui';

/**
 * The fields of an application, shared by the registration dialog and the
 * edit dialog. Both submit the same payload; only whether it is a POST or a
 * PATCH differs.
 */
export interface ApplicationFormValues {
  name: string;
  description: string;
  entraClientId: string;
  clientCredentials: boolean;
  delegated: boolean;
  apiKey: boolean;
  selfManagement: boolean;
  redirectUris: string;
  ipAllowlist: string;
}

export function emptyApplicationForm(): ApplicationFormValues {
  return {
    name: '',
    description: '',
    entraClientId: '',
    clientCredentials: false,
    delegated: false,
    apiKey: false,
    selfManagement: false,
    redirectUris: '',
    ipAllowlist: '',
  };
}

export function applicationFormFrom(application: Application): ApplicationFormValues {
  return {
    name: application.name,
    description: application.description ?? '',
    entraClientId: application.entra_client_id ?? '',
    clientCredentials: application.allow_client_credentials,
    delegated: application.allow_delegated,
    apiKey: application.allow_api_key,
    selfManagement: application.allow_self_management,
    redirectUris: application.redirect_uris.join('\n'),
    ipAllowlist: application.ip_allowlist.join('\n'),
  };
}

export function applicationPayload(values: ApplicationFormValues) {
  return {
    name: values.name,
    description: values.description || null,
    entraClientId: values.entraClientId || null,
    allowClientCredentials: values.clientCredentials,
    allowDelegated: values.delegated,
    allowApiKey: values.apiKey,
    allowSelfManagement: (values.clientCredentials || values.apiKey) && values.selfManagement,
    redirectUris: values.redirectUris
      .split(/\s+/)
      .map((value) => value.trim())
      .filter(Boolean),
    ipAllowlist: values.ipAllowlist
      .split(/\s+/)
      .map((value) => value.trim())
      .filter(Boolean),
  };
}

export function applicationFormComplete(values: ApplicationFormValues): boolean {
  const needsEntra = values.clientCredentials || values.delegated;
  return Boolean(values.name) && (!needsEntra || Boolean(values.entraClientId));
}

export function ApplicationFormFields({
  values,
  onChange,
}: {
  values: ApplicationFormValues;
  onChange: (values: ApplicationFormValues) => void;
}) {
  const set = <K extends keyof ApplicationFormValues>(key: K, value: ApplicationFormValues[K]) =>
    onChange({ ...values, [key]: value });
  const needsEntra = values.clientCredentials || values.delegated;

  return (
    <>
      <Field label="Name">
        <input
          type="text"
          value={values.name}
          onChange={(event) => set('name', event.target.value)}
          placeholder="SoluCoder"
        />
      </Field>
      <Field label="Description" hint="optional">
        <input type="text" value={values.description} onChange={(event) => set('description', event.target.value)} />
      </Field>

      <Field label="Access paths">
        <div>
          <Checkbox
            checked={values.clientCredentials}
            onChange={(value) => set('clientCredentials', value)}
            label="Application permissions (client_credentials) - the application acts for itself"
          />
          <Checkbox
            checked={values.delegated}
            onChange={(value) => set('delegated', value)}
            label="Delegated - users grant the application permissions in their name"
          />
          <Checkbox
            checked={values.apiKey}
            onChange={(value) => set('apiKey', value)}
            label="API key - for applications that do not speak Entra"
          />
          {values.clientCredentials || values.apiKey ? (
            <Checkbox
              checked={values.selfManagement}
              onChange={(value) => set('selfManagement', value)}
              label="Self-management - the application creates its own roles and hands them to its users, with its application token or a management key"
            />
          ) : null}
        </div>
      </Field>

      {needsEntra ? (
        <Field label="Entra client id" hint="from the app registration of the application">
          <input
            type="text"
            value={values.entraClientId}
            onChange={(event) => set('entraClientId', event.target.value)}
            placeholder="00000000-0000-0000-0000-000000000000"
          />
        </Field>
      ) : null}

      <Field
        label="Reachable from"
        hint="one address or range per line; empty means from anywhere. Holds for every access path above."
      >
        <textarea
          style={{ minHeight: 60 }}
          value={values.ipAllowlist}
          onChange={(event) => set('ipAllowlist', event.target.value)}
          placeholder={'203.0.113.5\n10.0.0.0/8'}
        />
      </Field>

      {values.delegated ? (
        <Field label="Redirect addresses" hint="one per line; only these are called after a consent">
          <textarea
            style={{ minHeight: 80 }}
            value={values.redirectUris}
            onChange={(event) => set('redirectUris', event.target.value)}
            placeholder="https://solucoder.solutec.dev/connected"
          />
        </Field>
      ) : null}
    </>
  );
}

/**
 * Which modules an application wants, and what for. The matrix is the same
 * one an administrator uses — asking for a permission and granting one should
 * not look like two different things.
 */
export function ModuleRulesPicker({
  modules,
  value,
  onChange,
}: {
  modules: SelfModule[];
  value: DesiredModule[];
  onChange: (modules: DesiredModule[]) => void;
}) {
  const chosen = new Map(value.map((entry) => [entry.moduleKey, entry]));

  function toggle(moduleKey: string, on: boolean): void {
    onChange(
      on ? [...value, { moduleKey, rules: [] }] : value.filter((entry) => entry.moduleKey !== moduleKey),
    );
  }

  function setRules(moduleKey: string, rules: GrantRule[]): void {
    onChange(
      value.map((entry) =>
        entry.moduleKey === moduleKey
          ? {
              moduleKey,
              rules: rules.map((rule) => ({
                resource: rule.resource,
                action: rule.action,
                effect: rule.effect,
              })),
            }
          : entry,
      ),
    );
  }

  if (!modules.length) return <Empty>No module is active yet.</Empty>;

  return (
    <>
      {modules.map((module) => {
        const entry = chosen.get(module.key);
        return (
          <div key={module.key} className="card-inset">
            <Checkbox
              checked={Boolean(entry)}
              onChange={(on) => toggle(module.key, on)}
              label={
                <>
                  {module.name} <span className="mono muted">{module.key}</span>
                </>
              }
            />
            {entry ? (
              <PermissionMatrix
                resources={module.resources}
                rules={entry.rules as GrantRule[]}
                onChange={(rules) => setRules(module.key, rules)}
              />
            ) : null}
          </div>
        );
      })}
    </>
  );
}
